import React, { useEffect, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

const FailedRecordReason: React.FC<{ reason?: string }> = ({ reason }) => {
  const [copyState, setCopyState] = useState<
    'idle' | 'copying' | 'copied' | 'error'
  >('idle');

  useEffect(() => {
    setCopyState('idle');
  }, [reason]);

  const copyReason = async () => {
    if (!reason) return;
    setCopyState('copying');
    try {
      await navigator.clipboard.writeText(reason);
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
  };

  if (!reason) return <Typography variant="body2">—</Typography>;

  return (
    <Box sx={{ width: 260, maxWidth: '100%' }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}
      >
        <Tooltip
          title={reason}
          componentsProps={{
            tooltip: {
              sx: {
                whiteSpace: 'pre-wrap',
                maxWidth: 480,
                maxHeight: 300,
                overflowY: 'auto',
                overflowWrap: 'anywhere',
              },
            },
          }}
        >
          <Typography
            variant="body2"
            noWrap
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              userSelect: 'text',
            }}
          >
            {reason}
          </Typography>
        </Tooltip>
        <Tooltip
          title={copyState === 'copied' ? 'Copied!' : 'Copy full reason'}
        >
          <span>
            <IconButton
              size="small"
              aria-label={
                copyState === 'copied'
                  ? 'Failure reason copied'
                  : 'Copy failure reason'
              }
              disabled={copyState === 'copying'}
              onClick={() => {
                void copyReason();
              }}
              sx={{
                flexShrink: 0,
                color: copyState === 'copied' ? 'success.main' : 'inherit',
              }}
            >
              {copyState === 'copied' ? (
                <CheckIcon fontSize="small" />
              ) : (
                <ContentCopyIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {copyState === 'error' && (
        <Typography variant="caption" color="error" role="alert">
          Could not copy reason. Please try again.
        </Typography>
      )}
    </Box>
  );
};

export default FailedRecordReason;
